import type { ReactNode } from "react";

/* What a list says when it has nothing in it.

   A grey "nothing yet" line reads as broken on a fresh instance, which is
   exactly when somebody is deciding whether this thing works. A small drawing,
   one sentence about what will appear here, and — where there is one — the
   button that makes it appear. The drawings are inline and stroke-only, so
   they take the theme's colours and cost nothing to load. */

type Art = "history" | "queue" | "air" | "search" | "soundboard" | "people";

const ART: Record<Art, ReactNode> = {
  history: (
    <>
      <rect x="10" y="12" width="44" height="12" rx="4" />
      <rect x="10" y="30" width="34" height="12" rx="4" opacity=".6" />
      <rect x="10" y="48" width="24" height="8" rx="4" opacity=".35" />
    </>
  ),
  queue: (
    <>
      <circle cx="16" cy="20" r="4" />
      <path d="M26 20h24" />
      <circle cx="16" cy="34" r="4" opacity=".6" />
      <path d="M26 34h18" opacity=".6" />
      <circle cx="16" cy="48" r="4" opacity=".35" />
      <path d="M26 48h12" opacity=".35" />
    </>
  ),
  air: (
    <>
      <path d="M18 26v12M26 20v24M34 14v36M42 22v20M50 28v8" />
    </>
  ),
  search: (
    <>
      <circle cx="28" cy="28" r="14" />
      <path d="m39 39 12 12" />
      <path d="M22 28h12" opacity=".5" />
    </>
  ),
  soundboard: (
    <>
      <rect x="10" y="12" width="18" height="18" rx="5" />
      <rect x="36" y="12" width="18" height="18" rx="5" opacity=".6" />
      <rect x="10" y="38" width="18" height="18" rx="5" opacity=".6" />
      <rect x="36" y="38" width="18" height="18" rx="5" opacity=".35" />
    </>
  ),
  people: (
    <>
      <circle cx="24" cy="24" r="8" />
      <path d="M10 52c2-9 8-13 14-13s12 4 14 13" />
      <circle cx="44" cy="26" r="6" opacity=".5" />
      <path d="M40 40c6 0 11 3 13 12" opacity=".5" />
    </>
  ),
};

export function EmptyState({
  art,
  title,
  body,
  action,
  compact = false,
}: {
  art: Art;
  title: string;
  body?: string;
  action?: { label: string; run: () => void };
  compact?: boolean;
}) {
  return (
    <div className={"empty-state" + (compact ? " compact" : "")}>
      <svg
        className="empty-art"
        viewBox="0 0 64 64"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        {ART[art]}
      </svg>
      <div className="empty-text">
        <div className="empty-title">{title}</div>
        {body && <div className="empty-body">{body}</div>}
        {action && (
          <button type="button" className="ghost" onClick={action.run}>
            {action.label}
          </button>
        )}
      </div>
    </div>
  );
}
